import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const WorkoutCalendarPanel = ({ upcomingDays, onSelectDay, selectedDayId }) => {
  const today = new Date();
  today?.setHours(0, 0, 0, 0);

  const getDayStatus = (day) => {
    const dayDate = new Date(day?.scheduled_date);
    dayDate?.setHours(0, 0, 0, 0);
    if (dayDate < today) return 'past';
    if (dayDate?.getTime() === today?.getTime()) return 'today';
    return 'upcoming';
  };

  const getCompletionRate = (day) => {
    const total = day?.workoutPlanExercises?.length || 0;
    const completed = day?.completions?.filter(c => c?.completed)?.length || 0;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  return (
    <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)' }}>
          <Icon name="CalendarDays" size={16} style={{ color: '#00D4FF' }} />
        </div>
        <h3 className="text-base font-bold" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>Upcoming Workouts</h3>
        <span className="ml-auto text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>{upcomingDays?.length} sessions</span>
      </div>
      {upcomingDays?.length === 0 ? (
        <div className="text-center py-6">
          <Icon name="Calendar" size={32} className="mx-auto mb-2" style={{ color: 'rgba(255,255,255,0.2)' }} />
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>No upcoming workouts scheduled</p>
          <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.25)' }}>Your trainer will assign workouts soon</p>
        </div>
      ) : (
        <div className="space-y-2">
          {upcomingDays?.slice(0, 7)?.map((day) => {
            const status = getDayStatus(day);
            const completionRate = getCompletionRate(day);
            const exerciseCount = day?.workoutPlanExercises?.length || 0;
            const isSelected = selectedDayId === day?.id;
            const dayDate = new Date(day?.scheduled_date);

            const isToday = status === 'today';
            const borderColor = isToday ? 'rgba(0,212,255,0.4)' : isSelected ? 'rgba(170,255,0,0.3)' : 'rgba(255,255,255,0.07)';
            const bgColor = isToday ? 'rgba(0,212,255,0.06)' : isSelected ? 'rgba(170,255,0,0.04)' : 'rgba(255,255,255,0.02)';
            const boxShadow = isToday ? '0 0 12px rgba(0,212,255,0.15)' : 'none';

            return (
              <motion.button
                key={day?.id}
                onClick={() => onSelectDay(day)}
                className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all"
                style={{ border: `1px solid ${borderColor}`, background: bgColor, boxShadow }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                {/* Date */}
                <div className="flex-shrink-0 w-12 text-center">
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>{daysOfWeek?.[dayDate?.getDay() === 0 ? 6 : dayDate?.getDay() - 1]}</p>
                  <p className="text-lg font-extrabold leading-tight" style={{ color: isToday ? '#00D4FF' : '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>{dayDate?.getDate()}</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>{dayDate?.toLocaleString('default', { month: 'short' })}</p>
                </div>
                {/* Divider */}
                <div className="w-px h-10 flex-shrink-0" style={{ background: 'rgba(255,255,255,0.08)' }} />
                {/* Workout info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>{day?.dayLabel || 'Workout'}</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>{exerciseCount} exercises</p>
                  {status === 'past' && completionRate > 0 && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${completionRate}%`,
                            background: completionRate === 100 ? '#AAFF00' : '#00D4FF',
                            boxShadow: completionRate === 100 ? '0 0 6px #AAFF0088' : '0 0 6px #00D4FF88',
                          }}
                        />
                      </div>
                      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>{completionRate}%</span>
                    </div>
                  )}
                </div>
                {/* Status indicator */}
                <div className="flex-shrink-0 flex items-center gap-1.5">
                  {status === 'today' && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ color: '#00D4FF', background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.25)' }}>Today</span>
                  )}
                  {status === 'past' && completionRate === 100 && (
                    <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(170,255,0,0.1)', border: '1px solid rgba(170,255,0,0.25)' }}>
                      <Icon name="Check" size={12} style={{ color: '#AAFF00' }} />
                    </div>
                  )}
                  {status === 'past' && completionRate < 100 && completionRate > 0 && (
                    <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,107,0,0.1)', border: '1px solid rgba(255,107,0,0.25)' }}>
                      <Icon name="Clock" size={12} style={{ color: '#FF6B00' }} />
                    </div>
                  )}
                  {status === 'upcoming' && (
                    <Icon name="ChevronRight" size={14} style={{ color: 'rgba(255,255,255,0.25)' }} />
                  )}
                </div>
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WorkoutCalendarPanel;
